import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import systemSettingsService from "../../services/systemSettingsService";

// Fetched once (see AdminLayout) and read anywhere the admin UI needs to
// convert a CI$ figure to its US$ equivalent for display — e.g. the admin
// dashboard/reports show everything in US$, while invoices themselves stay
// CI$ (what's actually billed) with a US$ equivalent shown alongside for
// the admin's own reference. This is a display-only conversion using
// whatever rate is current right now; it never gets baked into stored data
// (see reportController.resolveSailingFreightCi for why that distinction
// matters — this slice is the opposite case, always "as of now").
export const fetchSystemSettings = createAsyncThunk(
  "systemSettings/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await systemSettingsService.get();
      return data.data.settings;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message);
    }
  },
);

// Lightweight, any-authenticated-user endpoint (unlike fetchSystemSettings
// above, which needs manage_settings) — dispatched from CustomerLayout so
// the "Coming Soon" gate (see ComingSoon.jsx / AppRouter's FeatureGate) has
// an answer before the customer navigates to Promos/Referrals. Also
// dispatched from AdminLayout so the toggle on AdminSettings' Feature
// Flags panel reflects live state without a full settings fetch.
export const fetchFeatureFlags = createAsyncThunk(
  "systemSettings/fetchFeatureFlags",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await systemSettingsService.getFeatureFlags();
      return data.data.featureFlags;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message);
    }
  },
);

const systemSettingsSlice = createSlice({
  name: "systemSettings",
  initialState: {
    usdToCiRate: 1.2,
    loaded: false,
    loading: false,
    featureFlags: { promosEnabled: false, referralsEnabled: false },
    featureFlagsLoaded: false,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSystemSettings.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSystemSettings.fulfilled, (state, action) => {
        state.usdToCiRate = action.payload?.invoiceRates?.usdToCiRate || 1.2;
        if (action.payload?.featureFlags) state.featureFlags = action.payload.featureFlags;
        state.loaded = true;
        state.loading = false;
      })
      .addCase(fetchSystemSettings.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchFeatureFlags.fulfilled, (state, action) => {
        state.featureFlags = action.payload;
        state.featureFlagsLoaded = true;
      })
      .addCase(fetchFeatureFlags.rejected, (state) => {
        // Fails closed — an unreachable flags endpoint means "not
        // confirmed enabled", not "assume it's fine", so the Coming Soon
        // gate still has a definite (false) answer to render against
        // instead of hanging on featureFlagsLoaded forever.
        state.featureFlagsLoaded = true;
      });
  },
});

// 1 CI$ = this many US$ (official peg default 1.20) — see
// SystemSettings.invoiceRates.usdToCiRate / chargeCalculatorService.js.
export const selectUsdToCiRate = (state) => state.systemSettings.usdToCiRate;
export const selectFeatureFlags = (state) => state.systemSettings.featureFlags;
export const selectFeatureFlagsLoaded = (state) => state.systemSettings.featureFlagsLoaded;

export default systemSettingsSlice.reducer;
